// src/components/home/HomeContent.js
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useRef } from "react";
import moment from "moment";
import tour_guide_img from "./tour-guide.png";

//------------------------------------------------------------------------

const Container = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  position: relative;
`;

const FlexContainer = styled.div`
  flex: 1;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  overflow: hidden;
  padding: 10px;
`;

const ContentContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  @media (min-width: 500px) {
    flex-direction: row;
  }
  @media (max-width: 500px) {
    flex-direction: column;
  }
`;

const ItemBottomLine = styled.div`
  opacity: 0;
  position: absolute;
  bottom: 0;
  width: 0;
  height: 5px;
  background: #90a4ae;
  border-radius: 0 0 5px 5px;
  transition: width 0.3s, opacity 0.8s;
  z-index: 1;
`;

const Item = styled.div`
  user-select: none;
  flex: 1;
  height: 180px;
  min-height: 180px;
  width: 100%;
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  border-radius: 5px;
  background: #fafcfc;
  z-index: 1;

  span {
    font-weight: 600;
    font-size: 15px;
    color: #393939;
  }
  &:hover {
    cursor: pointer;
    i {
      color: ${({ theme }) => theme.colors.blue};
    }

    img {
      filter: sepia(1) saturate(30) hue-rotate(80deg) hue-rotate(80deg)
        contrast(0.6);
    }

    ${ItemBottomLine} {
      opacity: 1;
      width: 100%;
    }
  }
  i {
    color: #6d6d6d;
    font-size: 40px;
  }

  img {
    filter: brightness(0.43);
    width: 50px;
  }
`;

const InfoButton = styled.button`
  position: absolute;
  bottom: 15px;
  left: 15px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: #fafcfc;
  color: #6d6d6d;
  font-size: 18px;
  cursor: pointer;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.2);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f0f0f0;
    color: ${({ theme }) => theme.colors.blue};
  }
`;

const StyledDialog = styled.dialog`
  border: none;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 90%;

  &::backdrop {
    background: rgba(0, 0, 0, 0.5);
  }
`;

const DialogContent = styled.div`
  font-size: 13px;
  color: #393939;
  line-height: 1.8;

  div {
    margin-bottom: 8px;
  }

  strong {
    font-weight: 600;
  }

  a {
    color: ${({ theme }) => theme.colors.blue};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;

  h3 {
    margin: 0;
    font-size: 16px;
    color: #393939;
  }

  button {
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    color: #6d6d6d;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: #393939;
    }
  }
`;

//------------------------------------------------------------------------

const HomeContent = () => {
  const navigate = useNavigate();
  const dialogRef = useRef(null);

  const openDialog = () => {
    dialogRef.current?.showModal();
  };

  const closeDialog = () => {
    dialogRef.current?.close();
  };

  return (
    <Container>
      <FlexContainer>
        <ContentContainer>
          <Row>
            <Item
              onClick={() => {
                const today = moment().format("YYYY-MM-DD");
                navigate(`/tour_groups_calendar?startDate=${today}&sortBy=date`);
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <i className="fa-solid fa-calendar-days"></i>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <span>Tour Groups Calendar</span>
              </div>
              <ItemBottomLine />
            </Item>
            <Item
              onClick={() => {
                navigate("/bookings_manager");
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <i className="fa-solid fa-calendar"></i>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <span>Bookings Manager</span>
              </div>
              <ItemBottomLine />
            </Item>
            <Item
              onClick={() => {
                navigate("/day_planner");
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <i className="fa-solid fa-calendar-check"></i>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <span>Day Planner</span>
              </div>
              <ItemBottomLine />
            </Item>
          </Row>
          <Row>
            <Item
              onClick={() => {
                navigate("/external_assignments");
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <img src={tour_guide_img} alt="tour guide icon" />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <span>External Assignments</span>
              </div>
              <ItemBottomLine />
            </Item>
            <Item
              onClick={() => {
                navigate("/schedule_planner/staff");
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <i className="fa-solid fa-user-group"></i>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <span>Schedule</span>
              </div>
              <ItemBottomLine />
            </Item>
          </Row>
          <Row>
            <Item
              onClick={() => {
                navigate("/fleet");
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <i className="fa-solid fa-van-shuttle"></i>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <span>Fleet Management</span>
              </div>
              <ItemBottomLine />
            </Item>
            <Item
              onClick={() => {
                navigate("/announcements");
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <i className="fa-solid fa-bullhorn"></i>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <span>Announcements</span>
              </div>
              <ItemBottomLine />
            </Item>
          </Row>
          <Row>
            <Item
              onClick={() => {
                navigate("/booking_portal_logs");
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <i className="fa-solid fa-mobile-screen-button"></i>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <span>Booking Portal Logs</span>
              </div>
              <ItemBottomLine />
            </Item>
            <Item
              onClick={() => {
                navigate("/settings");
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <i className="fa-solid fa-sliders"></i>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <span>Settings</span>
              </div>
              <ItemBottomLine />
            </Item>
          </Row>
        </ContentContainer>
      </FlexContainer>
      <InfoButton onClick={openDialog}>
        <i className="fa-solid fa-info"></i>
      </InfoButton>
      <StyledDialog ref={dialogRef}>
        <DialogHeader>
          <h3>
            <u>Software Details</u>
          </h3>
          <button onClick={closeDialog}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </DialogHeader>
        <DialogContent>
          <div>
            <strong>Serial Number:</strong> GS-ERP-2024-001
          </div>
          <div>
            <strong>Registered to:</strong> Getaways Greece Private Company IKE
          </div>
          <div>
            <strong>VAT No:</strong> 800681442
          </div>
          <div>
            <strong>Registration Date:</strong> 01 July 2025
          </div>
          <div>
            <strong>Software:</strong> Getaways Suite ERP
          </div>
          <div>
            <strong>Author:</strong>{" "}
            <a href="https://app-ify.com" target="_blank" rel="noreferrer">
              APPIFY PC
            </a>
          </div>
        </DialogContent>
      </StyledDialog>
    </Container>
  );
};

export default HomeContent;
